import type { ApiError } from '@konyaxl/shared';

const BASE = '/api';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string>,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/** Oturum belleği. Token'ları tutan tek yer. */
let accessToken: string | null = null;
let refreshToken: string | null = null;

const STORAGE_KEY = 'konyaxl.session';

export function setTokens(access: string | null, refresh: string | null): void {
  accessToken = access;
  refreshToken = refresh;

  if (access && refresh) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ access, refresh }));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export function loadTokens(): boolean {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return false;

  try {
    const parsed = JSON.parse(raw) as { access?: string; refresh?: string };
    if (!parsed.access || !parsed.refresh) return false;
    accessToken = parsed.access;
    refreshToken = parsed.refresh;
    return true;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return false;
  }
}

export function hasSession(): boolean {
  return accessToken !== null;
}

/**
 * Access token süresi dolduğunda tek bir yenileme yapılır; o sırada gelen
 * diğer istekler aynı yenilemeyi bekler. Aksi halde eşzamanlı istekler
 * ayrı ayrı yenileme tetikler ve token rotasyonu birbirini geçersiz kılardı.
 */
let refreshInFlight: Promise<RefreshResult> | null = null;

type RefreshResult = 'ok' | 'invalid' | 'transient';

async function refreshSession(): Promise<RefreshResult> {
  if (!refreshToken) return 'invalid';

  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          setTokens(null, null);
          return 'invalid';
        }
        return 'transient';
      }

      const data = (await res.json()) as { accessToken: string; refreshToken: string };
      setTokens(data.accessToken, data.refreshToken);
      return 'ok';
    } catch {
      return 'transient';
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Yenileme sonrası tekrar denemeyi engeller (sonsuz döngü olmasın). */
  retried?: boolean;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, retried = false, signal } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (res.status === 401 && !retried && refreshToken) {
    const refreshResult = await refreshSession();
    if (refreshResult === 'ok') return request<T>(path, { ...options, retried: true });
    if (refreshResult === 'transient') {
      throw new ApiRequestError(
        0,
        'NETWORK_ERROR',
        'Sunucuya ulaşılamadı, oturum korunuyor',
      );
    }
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const err = data as Partial<ApiError>;
    throw new ApiRequestError(
      res.status,
      err.error?.code ?? 'ERROR',
      err.error?.message ?? 'Beklenmeyen bir hata oluştu',
      err.error?.fields,
      data,
    );
  }

  return data as T;
}

/**
 * Dosya indirir ve tarayıcıya kaydettirir.
 *
 * Basit bir `<a href>` bağlantısı kullanılamaz: indirme uçları da kimlik
 * doğrulaması istiyor ve tarayıcı gezinme isteklerine Authorization başlığı
 * eklemiyor. Bu yüzden dosya fetch ile alınıp geçici bir bağlantıyla
 * kaydediliyor.
 */
/**
 * Dosya indirir.
 *
 * `body` verilirse POST atılır: toplu sorgu gibi uçlarda girdi binlerce
 * satır olabiliyor ve sorgu dizesine sığmıyor.
 */
export async function downloadFile(
  path: string,
  fallbackName: string,
  body?: unknown,
): Promise<void> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const istek = (): RequestInit =>
    body === undefined
      ? { headers }
      : { method: 'POST', headers, body: JSON.stringify(body) };

  let res = await fetch(`${BASE}${path}`, istek());

  if (res.status === 401 && refreshToken && (await refreshSession())) {
    headers.Authorization = `Bearer ${accessToken}`;
    res = await fetch(`${BASE}${path}`, istek());
  }

  if (!res.ok) {
    throw new ApiRequestError(res.status, 'DOWNLOAD_FAILED', 'Dosya indirilemedi');
  }

  // Sunucunun önerdiği dosya adını kullan.
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? fallbackName;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  // Hemen iptal edilirse indirme yarıda kalabilir.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Yetki gerektiren bir dosyayı blob olarak indirir.
 *
 * Kayıt fotoğrafları doğrudan `<img src>` ile gösterilemiyor: uç, her istekte
 * Authorization başlığı bekliyor. Tarayıcı `img` isteğine başlık ekleyemediği
 * için içerik burada alınıp nesne URL'ine çevriliyor.
 */
export async function fetchBlob(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  let res = await fetch(`${BASE}${path}`, { headers });

  if (res.status === 401 && refreshToken && (await refreshSession())) {
    headers.Authorization = `Bearer ${accessToken}`;
    res = await fetch(`${BASE}${path}`, { headers });
  }

  if (!res.ok) throw new ApiRequestError(res.status, 'FETCH_FAILED', 'Dosya alınamadı');
  return res.blob();
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),
};
